export const VENDOR_ID_CACHE_KEY_PREFIX = 'customer_';
export const VENDOR_ID_CACHE_KEY_SUFFIX = '_device_vendor_ids';
export const VENDOR_ID_CACHE_TTL = 86400; // 24 hours in seconds

/**
 * Redis key prefix for the device asset cache. Consumed by:
 *   - `saas-device-api` `devices.module.ts` as the `AssetCacheService`
 *     `keyPrefix` config (drives `device:{id}` per-asset and
 *     `query:device:{hash}` query-result keys).
 *   - `saas-jobs/job-steps/devices/write-device-assets` for invalidating
 *     both per-asset and query keys after upserting fresh device data.
 *
 * Anything that writes device data to SQL must keep these caches honest;
 * anything that reads via `/device-assets` depends on this prefix.
 */
export const DEVICE_ASSET_CACHE_PREFIX = 'device';

/**
 * Redis keys for one sync's device telemetry (EDUS-3302).
 *
 * `list-device-telemetry` writes them and `write-sql-updates-device-assets`
 * reads them and deletes them after its last chunk. All three keys expire
 * after DEVICE_TELEMETRY_TTL_SECONDS, which must stay longer than
 * `full-asset-sync-workflow`'s worst case (6 hours) so a sync still running
 * never loses its telemetry.
 *
 * - Hash: `deviceId` → the device's raw telemetry JSON, gzipped, stored as a
 *   binary value. Read it with node-redis `returnBuffers`, or the bytes get
 *   decoded as UTF-8 and corrupted.
 * - Page token: the next `telemetry.devices.list` page, so a retried task
 *   resumes instead of starting over.
 * - Status: a DeviceTelemetryStatus. No status key means the job failed or
 *   hasn't finished; the write step then uses whatever is in the hash and
 *   carries stored telemetry over for everything else.
 */
export const DEVICE_TELEMETRY_TTL_SECONDS = 8 * 60 * 60;

export const DEVICE_TELEMETRY_STATUS = {
  COMPLETE: 'complete',
  SCOPE_NOT_GRANTED: 'scope_not_granted',
} as const;

export type DeviceTelemetryStatus =
  (typeof DEVICE_TELEMETRY_STATUS)[keyof typeof DEVICE_TELEMETRY_STATUS];

export function deviceTelemetryHashKey(
  customerId: number,
  workflowExecutionId: string
): string {
  return `telemetry:${customerId}:${workflowExecutionId}`;
}

export function deviceTelemetryPageTokenKey(
  customerId: number,
  workflowExecutionId: string
): string {
  return `${deviceTelemetryHashKey(customerId, workflowExecutionId)}:pageToken`;
}

export function deviceTelemetryStatusKey(
  customerId: number,
  workflowExecutionId: string
): string {
  return `${deviceTelemetryHashKey(customerId, workflowExecutionId)}:status`;
}
